/* eslint-disable @typescript-eslint/indent */
import styled, { css } from 'styled-components';

import { colors, spacing, typography, transitions } from '../../../styles/theme';

interface Props extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  disabled?: boolean;
  size?: 'small' | 'regular';
  hoverColor?: 'default' | 'alert';
  colorSchema?: 'default' | 'no-focus';
  color?: 'default' | 'primary' | 'accent';
  variant?: 'default' | 'flat';
  minWidth?: string;
  height?: number;
}

const bgColorMap = {
  default: 'default.base',
  accent: 'accent.base',
  primary: 'primary.base',
};

const textColorMap = {
  default: 'black.base',
  accent: 'accent.text',
  primary: 'primary.text',
};

function colorModifier({ color = 'default' }: Props) {
  return css`
    color: ${colors(textColorMap[color])};
    background-color: ${colors(bgColorMap[color])};
  `;
}

function variantModifier({ variant = 'default', color = 'default' }: Props) {
  if (variant === 'flat') {
    return css`
      color: ${colors(bgColorMap[color])};
      background-color: transparent;

      ${color === 'default' &&
      css`
        /* força texto preto para default */
        color: ${colors('black.base')} !important;
      `}

      &:hover,
      &:focus {
        color: ${colors(bgColorMap[color])};
        background-color: ${colors(bgColorMap[color])}33;
      }
    `;
  }

  return '';
}

const BaseButton = styled.button<Props>`
  font-size: ${typography('fontSize')};
  font-weight: 500;
  background-color: ${colors('default.base')};
  color: ${colors('black.base')};
  padding: ${spacing('2')};
  cursor: pointer;
  border: none;
  border-radius: 24px;
  text-decoration: none;
  font-family: inherit;
  transition: ${transitions('fast')};
  min-width: ${({ minWidth }: Props) => minWidth || 'auto'};
  height: 36px;
  max-height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;

  &:hover:enabled {
    background-color: ${colors('black.base')};
    color: ${colors('default.base')};
    transition: ${transitions('fast')};
  }

  ${({ disabled }: Props) =>
    disabled &&
    css`
      opacity: 0.5;
      cursor: not-allowed;

      :hover,
      :focus {
        background-color: ${colors('default.base')};
        color: ${colors('black.base')};
      }
    `}

  ${({ size }: Props) =>
    size === 'small' &&
    css`
      font-size: 14px;
      padding: ${spacing('1')};
    `}

  ${({ height }: Props) =>
    css`
      height: ${height}px;
    `}

  ${({ hoverColor }: Props) =>
    hoverColor === 'alert' &&
    css`
      :hover,
      :focus {
        background-color: ${colors('red.base')};
      }
    `}

  ${({ colorSchema }: Props) =>
    colorSchema === 'no-focus' &&
    css`
      background-color: ${colors('white.base')};
      color: ${colors('black.70')};
      border: 1px solid ${colors('black.08')};

      :hover,
      :focus {
        background-color: ${colors('white.base')};
        border-color: ${colors('black.base')};
        color: ${colors('black.base')};
      }
    `}

  ${colorModifier};
  ${variantModifier};

  & + & {
    margin-left: ${spacing('1')};
  }
`;

export default BaseButton;

interface AccentButtonProps {
  bgColor?: 'accent' | 'primary' | 'secondary';
  width?: string;
}

export const AccentButton = styled(BaseButton)<AccentButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: ${spacing('4')} auto;
  padding: ${spacing('2')} ${spacing('8')};
  background-color: ${({ bgColor }: AccentButtonProps) =>
    bgColor ? colors(`${bgColor}.base`) : colors('accent.base')};
  color: ${({ bgColor }: AccentButtonProps) => (bgColor ? colors(`${bgColor}.text`) : colors('accent.text'))};
  width: ${({ width }: AccentButtonProps) => width || '100%'};
  transition: filter ${transitions('fast')};

  &:hover:enabled {
    background-color: ${({ bgColor }: AccentButtonProps) =>
      bgColor ? colors(`${bgColor}.base`) : colors('accent.base')};
    color: ${({ bgColor }: AccentButtonProps) => (bgColor ? colors(`${bgColor}.text`) : colors('accent.text'))};
    transition: filter ${transitions('fast')};
    filter: ${transitions('effect.brightnessHigh')};
  }

  &:disabled {
    color: #777;
    opacity: 0.7;
    background-color: #ddd;
    cursor: not-allowed;
    box-shadow: none !important;
  }
`;
